"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

interface PaymentResultModalProps {
  onClose: () => void;
}

const PaymentResultModal = ({ onClose }: PaymentResultModalProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean | null>(null);

  useEffect(() => {
    const paymentResult = searchParams.get("payment_success");

    if (paymentResult === "true") {
      setPaymentSuccess(true);
      setIsVisible(true);

      // Disable body scrolling
      document.body.style.overflow = 'hidden';

        // Trigger confetti animation
        if (typeof window !== "undefined") {
          import("canvas-confetti").then((confetti) => {
            // Create custom canvas with specific z-index
            const canvas = document.createElement('canvas');
            canvas.style.position = 'fixed';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.width = '100vw';
            canvas.style.height = '100vh';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '10000'; // Above overlay (9999) but below modal (10001)
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            document.body.appendChild(canvas);

            const myConfetti = confetti.create(canvas, {
              resize: true,
              useWorker: true
            });

            const defaults = {
              spread: 360,
              ticks: 50,
              gravity: 0,
              decay: 0.94,
              startVelocity: 30,
              colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8']
            };

            function shoot() {
              myConfetti({
                ...defaults,
                particleCount: 40,
                scalar: 1.2,
                shapes: ['star']
              });

              myConfetti({
                ...defaults,
                particleCount: 10,
                scalar: 0.75,
                shapes: ['circle']
              });
            }

            setTimeout(shoot, 0);
            setTimeout(shoot, 100);
            setTimeout(shoot, 200);

            // Clean up canvas after animation
            setTimeout(() => {
              if (canvas && canvas.parentNode && canvas.parentNode.contains(canvas)) {
                try {
                  canvas.parentNode.removeChild(canvas);
                } catch (e) {
                  // Silently ignore if already removed
                }
              }
            }, 3000);
          });
        }
    } else if (paymentResult === "false") {
      setPaymentSuccess(false);
      setIsVisible(true);

      // Disable body scrolling
      document.body.style.overflow = 'hidden';
    }
  }, [searchParams]);

  const handleClose = () => {
    setIsVisible(false);
    // Re-enable body scrolling
    document.body.style.overflow = 'unset';
    // Clean up URL
    router.replace("/dashboard");
    onClose();
  };

  // Cleanup effect to re-enable scrolling if component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!isVisible || paymentSuccess === null) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9000] flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 scale-100 z-50 relative">
          {paymentSuccess ? (
            // Success Modal
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                🎉 Payment Successful!
              </h3>
              <p className="text-gray-600 mb-6">
                Welcome to PFPMaker Pro! Your payment was successful and we have added credits to your account.
              </p>

              {/* Success Button */}
              <button
                onClick={handleClose}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-5 px-6 rounded-lg transition-colors duration-200"
              >
                Start Creating!
              </button>
            </div>
          ) : (
            // Failure Modal
            <div className="p-8 text-center">
              {/* Error Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>

              {/* Error Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Payment Canceled
              </h3>
              <p className="text-gray-600 mb-6">
                No charges were made to your account. Please try again or contact support at: <strong>support@pfpmaker.com</strong>
              </p>

              {/* Error Button */}
              <button
                onClick={handleClose}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-5 px-6 rounded-lg transition-colors duration-200"
              >
                Continue to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentResultModal;
